<template>
	<div
		class="stock-info-box-left"
		:style="{
			...getMarketColorVars,
			...getTrendColorVars
		}"
	>
		<div class="logo-img"><img :src="logo" alt="" /></div>
		<div class="organization-info">
			<p class="organization">{{ getOrganizationName }}</p>
			<div class="stock-code-box">
				<span class="stock-mk">{{ getBaseData.market.toLocaleUpperCase() }}</span>
				<span class="stock-code">{{ getBaseData.code }}</span>
			</div>
		</div>
		<div class="price-info">
			<p class="price" :class="[props.stockQuoteData.change > 0 ? 'price--up' : 'price--down']">
				<span class="close-price">{{ props.stockQuoteData.closePrice }} </span
				><span class="symbol">{{ symbol }}</span
				><span class="price-change"
					>{{ props.stockQuoteData.change > 0 ? '+' : '' }}{{ props.stockQuoteData.change?.toFixed(2) }}</span
				><em class="gap"></em
				><span class="price-change">{{ props.stockQuoteData.changePct?.toFixed(2) + '%' }}</span>
			</p>

			<div class="price-date">
				<slot v-if="'footer-date' in slots" name="footer-date"></slot>
				<span v-else>{{ formatDateFn(props.stockQuoteData.tradingDay) }}</span>
			</div>
		</div>
	</div>
</template>

<script setup>
import { computed, getCurrentInstance, useSlots } from 'vue';
import { useStockVO } from '@rebuild/hooks/useStockVO';
import { useCommonData } from '@/hooks';
import dayjs from 'dayjs';
import { useStore } from '@/utils/vue-api';
import { useMarketColors } from '@rebuild/materials/shared/quote/useMarketColors';

const slots = useSlots();

const { getMarketColorVars, getTrendColorVars } = useMarketColors();

const { getOrganizationName, getColumnData } = useCommonData();
const { logo } = getColumnData.value;

const store = useStore();
const getBaseData = computed(() => store.getters.getBaseData);

const { isHKStock } = useStockVO();

const { proxy } = getCurrentInstance();

// Props
const props = defineProps({
	stockQuoteData: {
		type: Object,
		default: () => ({})
	}
});

const symbol = computed(() => store.getters.getCurrUnitTips);

const formatDateFn = date => {
	const formatTime = dayjs(date).valueOf();
	const formatValue = dayjs(formatTime).format('YYYY-MM-DD');

	return `${proxy.$t('enterprise.common-a.until')} ${formatValue} ${isHKStock.value ? '16:00' : '15:00'} （${proxy.$t(
		'enterprise.UTC'
	)}）`;
};
</script>

<style lang="less" scoped>
.stock-info-box-left {
	display: flex;
	align-items: center;
	.logo-img {
		width: 58px;
		height: 58px;
		padding: 8px;
		border-radius: 100px;
		border: 0.5px solid var(--border-normal-color, #e5e6eb);
		img {
			width: 100%;
			height: 100%;
			border-radius: 40px;
		}
	}
	.organization-info {
		padding: 0 12px 0 8px;
		.organization {
			color: var(--t-emphasis-color, #1d2129);
			font-size: 24px;
			font-weight: 500;
			line-height: 32px; /* 133.333% */
		}
		.stock-code-box {
			margin-top: 4px;
			display: flex;
			align-items: center;
			.stock-mk {
				padding: 2px 8px;
				font-size: 10px;
				font-weight: 600;
				line-height: 14px; /* 140% */

				color: var(--market-primary-color);
				background: var(
					---,
					linear-gradient(0deg, rgba(255, 255, 255, 0.9) 0%, rgba(255, 255, 255, 0.9) 100%),
					var(--market-primary-color)
				);
			}
			.stock-code {
				padding: 2px 8px;
				color: var(--t-secondary-color, #4e5969);
				font-size: 10px;
				font-weight: 600;
				line-height: 14px; /* 140% */
				background: var(--fill-normal-color, #f2f3f5);
			}
		}
	}
	.price-info {
		.price {
			display: flex;
			align-items: baseline;

			&--up {
				color: var(--market-trend-color--up);
			}
			&--down {
				color: var(--market-trend-color--down);
			}

			.close-price {
				font-family: D-DIN;
				font-size: 44px;
				font-weight: 700;
				line-height: 44px; /* 100% */
			}
			.symbol {
				padding: 0 8px 0 4px;
				font-size: 16px;
				font-weight: 500;
				line-height: 16px; /* 100% */
			}
			.price-change {
				font-family: D-DIN;
				font-size: 20px;
				font-weight: 700;
				line-height: 20px; /* 100% */
			}
			.gap {
				margin: 0 12px;
				width: 1px;
				height: 16px;
				background: var(--border-normal-color, #e5e6eb);
			}
		}
		.price-date {
			padding: 4px 0 8px;
			color: var(--t-secondary-info-color, #86909c);
			font-size: 10px;
			font-weight: 400;
			line-height: 10px; /* 100% */
		}
	}
}
</style>
